import * as RadixSelect from '@radix-ui/react-select';
import DropdownUi, {
	DropdownUiTriggerProps,
} from 'src/components/primitives/DropdownUi/DropdownUi';
import { DROPDOWN_SIDE_OFFSET } from 'src/components/primitives/DropdownUi/consts';
import { cm } from 'src/utils/classNames';
import { WithRef } from 'src/utils/UtilityTypes';

function Trigger({
	size = 'sm',
	className,
	labelClassName,
	valueClassName,
	label,
	disabled,
	children,
	dataTestId,
	...rest
}: WithRef<
	DropdownUiTriggerProps & { dataTestId?: string },
	HTMLButtonElement
>) {
	return (
		<RadixSelect.Trigger asChild disabled={disabled} {...rest}>
			<DropdownUi.Trigger
				size={size}
				className={className}
				labelClassName={labelClassName}
				valueClassName={valueClassName}
				label={label}
				disabled={disabled}
				data-testid={dataTestId}
			>
				<span className="truncate">{children}</span>
				<RadixSelect.Icon asChild>
					<DropdownUi.Chevron />
				</RadixSelect.Icon>
			</DropdownUi.Trigger>
		</RadixSelect.Trigger>
	);
}

export interface SelectOptionsProps extends Pick<
	RadixSelect.SelectContentProps,
	| 'asChild'
	| 'onCloseAutoFocus'
	| 'onEscapeKeyDown'
	| 'onPointerDownOutside'
	| 'position'
	| 'side'
	| 'sideOffset'
	| 'align'
	| 'alignOffset'
	| 'avoidCollisions'
	| 'collisionBoundary'
	| 'collisionPadding'
	| 'arrowPadding'
	| 'sticky'
	| 'hideWhenDetached'
	| 'children'
	| 'className'
> {
	scrollContainerClassName?: string;
}

function Options({
	children,
	className,
	position = 'popper',
	sideOffset = DROPDOWN_SIDE_OFFSET,
	scrollContainerClassName,
	align = 'center',
	...rest
}: WithRef<SelectOptionsProps, HTMLDivElement>) {
	return (
		<RadixSelect.Portal>
			<RadixSelect.Content
				position={position}
				sideOffset={sideOffset}
				align={align}
				// Prevent focus ring from appearing when closed with pointer.
				onCloseAutoFocus={(e) => e.preventDefault()}
				asChild
				{...rest}
			>
				<DropdownUi.Content
					className={cm('min-w-(--radix-select-trigger-width)', className)}
				>
					<RadixSelect.Viewport
						className={cm('flex flex-col', scrollContainerClassName)}
					>
						{children}
					</RadixSelect.Viewport>
				</DropdownUi.Content>
			</RadixSelect.Content>
		</RadixSelect.Portal>
	);
}

export interface SelectOptionProps extends Pick<
	RadixSelect.SelectItemProps,
	'asChild' | 'value' | 'disabled' | 'textValue' | 'children' | 'className'
> {
	noSelectedCheckmark?: boolean;
	dataTestId?: string;
}

function Option({
	value,
	className,
	children,
	disabled,
	noSelectedCheckmark,
	dataTestId,
	...rest
}: WithRef<SelectOptionProps, HTMLDivElement>) {
	return (
		<RadixSelect.Item value={value} disabled={disabled} asChild {...rest}>
			<DropdownUi.Item
				className={className}
				disabled={disabled}
				data-testid={dataTestId}
			>
				{children}
				{!noSelectedCheckmark && (
					<RadixSelect.ItemIndicator asChild>
						<DropdownUi.SelectedCheckmark />
					</RadixSelect.ItemIndicator>
				)}
			</DropdownUi.Item>
		</RadixSelect.Item>
	);
}

export default {
	Root: RadixSelect.Root,
	Trigger,
	Options,
	Option,
};
